import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nexus/ui";
import { GripVertical, Star, Trash2 } from "lucide-react";
import type { DragHandleProps, EditablePackage } from "../lib/admin-vendor-form.utils";
import { VENDOR_FORM_LIMITS } from "../lib/admin-vendor-form.utils";
import { PACKAGE_CADENCE_OPTIONS } from "../../lib/package-cadence";

/** Radix Select forbids empty item values, so map "no cadence" to a sentinel. */
const CADENCE_NONE = "__none__";

interface PackageEditorProps {
  pkg: EditablePackage;
  index: number;
  onChange: (patch: Partial<EditablePackage>) => void;
  onRemove: () => void;
  onMarkPopular: () => void;
  dragHandle?: DragHandleProps;
}

export function PackageEditor({
  pkg,
  index,
  onChange,
  onRemove,
  onMarkPopular,
  dragHandle,
}: PackageEditorProps) {
  const { t } = useTranslation();
  // Incomplete code (1–2 chars). Empty = no currency (allowed); 3 = valid.
  const currencyInvalid =
    !!pkg.currency && pkg.currency.length !== VENDOR_FORM_LIMITS.currencyCodeLength;

  return (
    <div className="group/pkg relative -mx-3 rounded-lg px-3 py-4 transition-colors first:pt-1 hover:bg-muted/30">
      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        <DragHandle dragHandle={dragHandle} />
        <span className="text-xs font-semibold tabular-nums text-foreground">
          {t("marketplace.admin.form.packageN", {
            n: index + 1,
            defaultValue: "Package {{n}}",
          })}
        </span>
        {pkg.isPopular && (
          <Badge variant="ai" iconStart="star" className="text-[10px]">
            {t("marketplace.admin.form.packageIsPopular")}
          </Badge>
        )}
        {pkg.isActive === false && (
          <Badge variant="slate" className="text-[10px]">
            {t("marketplace.admin.form.hiddenBadge", { defaultValue: "Hidden" })}
          </Badge>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover/pkg:opacity-100"
              onClick={onRemove}
              aria-label={t("marketplace.admin.form.removePackage", {
                defaultValue: "Remove package",
              })}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("marketplace.admin.form.removePackage", { defaultValue: "Remove package" })}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 pl-7">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${pkg.uid}-name`}>{t("marketplace.admin.form.packageName")}</Label>
          <Input
            id={`${pkg.uid}-name`}
            required
            value={pkg.name}
            placeholder={t("marketplace.admin.form.packageNamePlaceholder", {
              defaultValue: "e.g. Starter",
            })}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${pkg.uid}-desc`}>
            {t("marketplace.admin.form.packageDescription")}
          </Label>
          <Textarea
            id={`${pkg.uid}-desc`}
            rows={2}
            value={pkg.description ?? ""}
            onChange={(e) => onChange({ description: e.target.value || null })}
          />
        </div>

        {/* Price row — labelled, with currency adornment grouping */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${pkg.uid}-price`} className="text-xs text-muted-foreground">
              {t("marketplace.admin.form.packagePrice")}
            </Label>
            <Input
              id={`${pkg.uid}-price`}
              type="number"
              min={0}
              step={0.01}
              inputMode="decimal"
              placeholder="0.00"
              value={pkg.price ?? ""}
              onChange={(e) =>
                onChange({ price: e.target.value ? Number(e.target.value) : null })
              }
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${pkg.uid}-currency`} className="text-xs text-muted-foreground">
              {t("marketplace.admin.form.packageCurrency")}
            </Label>
            <Input
              id={`${pkg.uid}-currency`}
              maxLength={VENDOR_FORM_LIMITS.currencyCodeLength}
              placeholder="USD"
              inputMode="text"
              autoCapitalize="characters"
              aria-invalid={currencyInvalid}
              value={pkg.currency ?? ""}
              onChange={(e) => {
                // ISO 4217 codes are 3 letters — strip anything else so numbers can't be entered.
                const next = e.target.value
                  .replace(/[^a-zA-Z]/g, "")
                  .toUpperCase()
                  .slice(0, VENDOR_FORM_LIMITS.currencyCodeLength);
                onChange({ currency: next || null });
              }}
              className="uppercase sm:w-20"
            />
            {currencyInvalid && (
              <span className="text-[10px] text-destructive">
                {t("marketplace.admin.form.currencyInvalid", {
                  count: VENDOR_FORM_LIMITS.currencyCodeLength,
                  defaultValue: "Use a {{count}}-letter code (e.g. USD).",
                })}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${pkg.uid}-cadence`} className="text-xs text-muted-foreground">
              {t("marketplace.admin.form.packageCadence")}
            </Label>
            <Select
              value={pkg.cadence ?? CADENCE_NONE}
              onValueChange={(v) =>
                onChange({ cadence: v === CADENCE_NONE ? null : v })
              }
            >
              <SelectTrigger id={`${pkg.uid}-cadence`}>
                <SelectValue
                  placeholder={t("marketplace.admin.form.packageCadencePlaceholder", {
                    defaultValue: "Select cadence",
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CADENCE_NONE}>
                  {t("marketplace.admin.form.cadenceNone", {
                    defaultValue: "No cadence",
                  })}
                </SelectItem>
                {PACKAGE_CADENCE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-border/60 pt-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={pkg.isPopular ?? false} onCheckedChange={() => onMarkPopular()} />
            <Star className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            {t("marketplace.admin.form.packageIsPopular")}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Switch
              checked={pkg.isActive ?? true}
              onCheckedChange={(v) => onChange({ isActive: v })}
            />
            {t("marketplace.admin.form.packageIsActive")}
          </label>
        </div>
      </div>
    </div>
  );
}

function DragHandle({ dragHandle }: { dragHandle?: DragHandleProps }) {
  const { t } = useTranslation();
  if (!dragHandle) {
    return <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden />;
  }
  return (
    <button
      type="button"
      aria-label={t("common.dragToReorder")}
      className="-ml-1 flex h-7 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/50 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
      {...dragHandle.attributes}
      {...dragHandle.listeners}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}
